import { ChangeDetectionStrategy, Component } from '@angular/core';
import { MsalBroadcastService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';
import { EnvironmentConfigurationService } from '@nimbus-library/features/configuration';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { AuthService } from '../../services/auth.service';

@UntilDestroy()
@Component({
  standalone: false,
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginComponent {
  // The background keep-alive poll drives SsoSilent/AcquireToken through MSAL every ~20s while the
  // user sits on this page (tryLogInSilently). Disabling the button for those statuses too made
  // "Sign in" briefly unresponsive on an unlucky click, with no user-facing cause. Only the statuses
  // that come from this page's own login() (Login, plus the initial Startup/HandleRedirect) should
  // gate the button.
  private static readonly runningStatuses: ReadonlySet<InteractionStatus> = new Set([
    InteractionStatus.Startup,
    InteractionStatus.Login,
    InteractionStatus.HandleRedirect,
  ]);

  public isRunning$: BehaviorSubject<boolean>;

  public appVersion: string | undefined;
  public environmentName: string;
  public TranslationKeys = TranslationKeys;

  public constructor(
    private readonly authService: AuthService,
    private readonly msalBroadcastService: MsalBroadcastService,
    environmentService: EnvironmentConfigurationService,
  ) {
    this.isRunning$ = new BehaviorSubject<boolean>(false);
    this.appVersion = environmentService.configurationSettings.releaseVersion;
    this.environmentName = environmentService.configurationSettings.environmentName;

    this.msalBroadcastService.inProgress$.pipe(untilDestroyed(this)).subscribe((status) => {
      this.isRunning$.next(LoginComponent.runningStatuses.has(status));
    });
  }

  public login(): void {
    this.isRunning$.next(true);
    this.authService.login();
  }
}
